import { useState, useCallback } from 'react';
import pptxgen from 'pptxgenjs';
import type { SlidesDeck, Slide } from '@/lib/content-studio/types';

// ── Modern design system ──────────────────────────────────────────────
// A cohesive, editorial look: a coloured accent "spine" on every content
// slide, a refined type hierarchy, generously-spaced bullets, soft rounded
// cards for comparisons, and framed imagery. Tuned to read as a designed deck
// rather than a default template.
const FONT_HEAD = 'Calibri';   // clean, modern, Office-native (safe on Windows/Mac)
const FONT_BODY = 'Calibri';
const INK = '0F172A';          // slate-900 — headings
const BODY = '334155';         // slate-700 — body copy
const MUTED = '94A3B8';        // slate-400 — captions / footer
const CARD = 'F8FAFC';         // slate-50 — card fills
const CARD_LINE = 'E2E8F0';    // slate-200 — hairlines

// Helper to lighten a hex color
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex, 16);
  const r = Math.min(255, ((num >> 16) + percent * 2.55) | 0);
  const g = Math.min(255, (((num >> 8) & 0x00FF) + percent * 2.55) | 0);
  const b = Math.min(255, ((num & 0x0000FF) + percent * 2.55) | 0);
  return ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

interface UsePptxGeneratorReturn {
  generatePptx: (deck: SlidesDeck) => Promise<void>;
  isGenerating: boolean;
  error: Error | null;
}

export function usePptxGenerator(): UsePptxGeneratorReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generatePptx = useCallback(async (deck: SlidesDeck) => {
    setIsGenerating(true);
    setError(null);

    try {
      const pptx = new pptxgen();
      pptx.defineLayout({ name: 'WIDE', width: 10, height: 5.625 });
      pptx.layout = 'WIDE';

      pptx.author = deck.author || 'Content Studio';
      pptx.title = deck.title;
      pptx.subject = deck.subtitle || '';
      pptx.company = 'MEDD-SIM';

      const primaryColor = (deck.theme?.primaryColor?.replace('#', '') || '1e40af').toLowerCase();
      const accentColor = lightenColor(primaryColor, 35);

      // Content master: white canvas + a slim coloured spine on the left edge
      // and a minimal footer. The spine is the signature "modern" element.
      pptx.defineSlideMaster({
        title: 'CONTENT_MASTER',
        background: { color: 'FFFFFF' },
        objects: [
          { rect: { x: 0, y: 0, w: 0.14, h: 5.625, fill: { color: primaryColor } } },
          { text: { text: deck.title, options: {
            x: 0.55, y: 5.28, w: 7.5, h: 0.25, fontSize: 8, color: MUTED,
            align: 'left', fontFace: FONT_BODY,
          } } },
        ],
      });

      pptx.defineSlideMaster({
        title: 'TITLE_MASTER',
        background: { color: primaryColor },
        objects: [
          // subtle darker band at the base for depth
          { rect: { x: 0, y: 5.0, w: '100%', h: 0.625, fill: { color: lightenColor(primaryColor, -12) } } },
        ],
      });

      const totalSlides = deck.slides.length;
      for (let i = 0; i < deck.slides.length; i++) {
        const slide = deck.slides[i];
        const isFirstSlide = i === 0 && slide.layout === 'title';
        const pptSlide = pptx.addSlide({ masterName: isFirstSlide ? 'TITLE_MASTER' : 'CONTENT_MASTER' });

        // Number badge (pill) on content slides
        if (!isFirstSlide) {
          pptSlide.addText(`${String(i + 1).padStart(2, '0')}`, {
            x: 9.05, y: 0.32, w: 0.6, h: 0.34,
            fontSize: 11, bold: true, color: primaryColor, align: 'center', valign: 'middle',
            fontFace: FONT_HEAD, fill: { color: CARD }, line: { color: CARD_LINE, width: 0.5 },
            rectRadius: 0.17, shape: 'roundRect',
          });
        }

        await addSlideContent(pptSlide, slide, primaryColor, accentColor, isFirstSlide);

        if (slide.speakerNotes) pptSlide.addNotes(slide.speakerNotes);
      }

      const fileName = `${deck.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pptx`;
      await pptx.writeFile({ fileName });
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to generate PowerPoint');
      setError(e);
      throw e;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return { generatePptx, isGenerating, error };
}

// Title + short accent underline — the standard header for content slides.
function addHeader(pptSlide: pptxgen.Slide, title: string, primaryColor: string, accentColor: string) {
  pptSlide.addText(title, {
    x: 0.55, y: 0.4, w: 8.3, h: 0.8,
    fontSize: 30, bold: true, color: INK, align: 'left', valign: 'middle', fontFace: FONT_HEAD,
  });
  pptSlide.addShape('rect', { x: 0.6, y: 1.18, w: 0.9, h: 0.06, fill: { color: accentColor } });
}

async function addSlideContent(
  pptSlide: pptxgen.Slide,
  slide: Slide,
  primaryColor: string,
  accentColor: string,
  isTitleSlide: boolean = false,
) {
  const { layout, title, content } = slide;

  switch (layout) {
    case 'title':
      if (isTitleSlide) {
        pptSlide.addShape('rect', { x: 0.85, y: 2.35, w: 1.1, h: 0.09, fill: { color: 'FFFFFF' } });
        pptSlide.addText(title, {
          x: 0.85, y: 2.5, w: 8.3, h: 1.5, fontSize: 46, bold: true, color: 'FFFFFF',
          align: 'left', valign: 'middle', fontFace: FONT_HEAD,
        });
        if (content.mainText) {
          pptSlide.addText(content.mainText, {
            x: 0.85, y: 3.95, w: 8.0, h: 0.9, fontSize: 20, color: 'E0E7FF',
            align: 'left', valign: 'top', fontFace: FONT_BODY,
          });
        }
      } else {
        pptSlide.addText(title, {
          x: 0.85, y: 2.0, w: 8.3, h: 1.5, fontSize: 40, bold: true, color: primaryColor,
          align: 'left', valign: 'middle', fontFace: FONT_HEAD,
        });
        pptSlide.addShape('rect', { x: 0.9, y: 3.35, w: 1.1, h: 0.07, fill: { color: accentColor } });
        if (content.mainText) {
          pptSlide.addText(content.mainText, {
            x: 0.85, y: 3.6, w: 8.0, h: 1, fontSize: 20, color: BODY, align: 'left', valign: 'top', fontFace: FONT_BODY,
          });
        }
      }
      break;

    case 'bullets':
      addHeader(pptSlide, title, primaryColor, accentColor);
      if (content.bullets && content.bullets.length > 0) {
        const items = content.bullets.map(b => ({
          text: b,
          options: {
            fontSize: 18, color: BODY, fontFace: FONT_BODY,
            bullet: { characterCode: '25AA', indent: 22 }, // small filled square marker
            paraSpaceAfter: 14, paraSpaceBefore: 0,
          },
        }));
        pptSlide.addText(items as pptxgen.TextProps[], { x: 0.6, y: 1.5, w: 8.6, h: 3.6, valign: 'top', color: primaryColor });
      }
      break;

    case 'content':
      addHeader(pptSlide, title, primaryColor, accentColor);
      if (content.mainText) {
        pptSlide.addText(content.mainText, {
          x: 0.6, y: 1.55, w: 8.6, h: 3.6, fontSize: 19, color: BODY, valign: 'top',
          fontFace: FONT_BODY, lineSpacingMultiple: 1.25,
        });
      }
      break;

    case 'two-column': {
      addHeader(pptSlide, title, primaryColor, accentColor);
      const cardY = 1.5, cardH = 3.5, leftX = 0.6, rightX = 5.15, cardW = 4.25;
      const makeCard = (x: number, items?: string[]) => {
        if (!items || items.length === 0) return;
        pptSlide.addShape('roundRect', { x, y: cardY, w: cardW, h: cardH, rectRadius: 0.1, fill: { color: CARD }, line: { color: CARD_LINE, width: 1 } });
        const body = items.map(it => ({
          text: it,
          options: { fontSize: 15, color: BODY, fontFace: FONT_BODY, bullet: { characterCode: '25AA', indent: 20 }, paraSpaceAfter: 10 },
        }));
        pptSlide.addText(body as pptxgen.TextProps[], { x: x + 0.3, y: cardY + 0.3, w: cardW - 0.6, h: cardH - 0.6, valign: 'top', color: primaryColor });
      };
      makeCard(leftX, content.leftColumn);
      makeCard(rightX, content.rightColumn);
      break;
    }

    case 'quote': {
      // Tinted panel + oversized quotation mark for an editorial pull-quote.
      pptSlide.addShape('roundRect', { x: 0.6, y: 1.1, w: 8.6, h: 3.5, rectRadius: 0.12, fill: { color: CARD }, line: { color: CARD_LINE, width: 1 } });
      pptSlide.addText('“', { x: 0.8, y: 0.9, w: 1.5, h: 1.5, fontSize: 96, bold: true, color: accentColor, fontFace: 'Georgia' });
      if (content.quote) {
        pptSlide.addText(content.quote, {
          x: 1.4, y: 1.7, w: 7.2, h: 2, fontSize: 26, italic: true, color: INK, align: 'left', valign: 'middle', fontFace: 'Georgia', lineSpacingMultiple: 1.15,
        });
      }
      if (content.attribution) {
        pptSlide.addText(`— ${content.attribution}`, {
          x: 1.4, y: 3.9, w: 7.2, h: 0.5, fontSize: 15, bold: true, color: primaryColor, align: 'left', fontFace: FONT_BODY,
        });
      }
      break;
    }

    case 'image-text': {
      addHeader(pptSlide, title, primaryColor, accentColor);
      const imgX = 0.6, imgY = 1.5, imgW = 4.6, imgH = 3.4;
      let placed = false;
      if (content.generatedImageUrl) {
        try {
          const data = content.generatedImageUrl.startsWith('data:image')
            ? content.generatedImageUrl
            : await fetchImageAsBase64(content.generatedImageUrl);
          if (data) {
            pptSlide.addImage({ data, x: imgX, y: imgY, w: imgW, h: imgH, rounding: true });
            placed = true;
          }
        } catch (err) {
          console.error('Failed to embed image in PPTX:', err);
        }
      }
      if (!placed) addImagePlaceholder(pptSlide, imgX, imgY, imgW, imgH, content.imagePrompt);

      if (content.mainText) {
        pptSlide.addText(content.mainText, {
          x: 5.5, y: 1.55, w: 3.9, h: 3.3, fontSize: 17, color: BODY, valign: 'top', fontFace: FONT_BODY, lineSpacingMultiple: 1.25,
        });
      }
      break;
    }

    default:
      addHeader(pptSlide, title, primaryColor, accentColor);
      if (content.mainText) {
        pptSlide.addText(content.mainText, {
          x: 0.6, y: 1.55, w: 8.6, h: 3.6, fontSize: 19, color: BODY, valign: 'top', fontFace: FONT_BODY, lineSpacingMultiple: 1.25,
        });
      }
      break;
  }
}

function addImagePlaceholder(pptSlide: pptxgen.Slide, x: number, y: number, w: number, h: number, imagePrompt?: string) {
  pptSlide.addShape('roundRect', { x, y, w, h, rectRadius: 0.1, fill: { color: CARD }, line: { color: CARD_LINE, width: 1 } });
  pptSlide.addText(imagePrompt || 'Image', { x: x + 0.3, y: y + h / 2 - 0.4, w: w - 0.6, h: 0.8, fontSize: 12, color: MUTED, align: 'center', fontFace: FONT_BODY });
}

async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Failed to fetch image:', response.status);
      return null;
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error('Error fetching image for PPTX:', err);
    return null;
  }
}

export default usePptxGenerator;
